
import { useState, useMemo } from 'react';
import { toPascalCase } from "@/utils/text";
import { EnrichedVisitor } from "@/types/database";
import { VisitorsFilters } from "../VisitorsFilterBar";

// Predefined options for filters
const DEFAULT_INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Financial Services',
  'Education',
  'Manufacturing',
  'Retail',
  'Real Estate',
  'Construction',
  'Transportation',
  'Media',
  'Energy',
  'Agriculture',
  'Hospitality',
  'Telecommunications',
  'Entertainment',
  'Consulting'
];

const DEFAULT_COMPANY_SIZES = [
  'Self-employed',
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10000+'
];

const DEFAULT_LOCATIONS = [
  'United States',
  'United Kingdom',
  'Canada',
  'Germany',
  'France',
  'Australia',
  'Japan',
  'India',
  'Brazil',
  'Spain',
  'Italy',
  'Netherlands',
  'Sweden',
  'Singapore',
  'Ireland'
];

const DEFAULT_PERSON_ROLES = [
  'C-Level Executive',
  'Vice President',
  'Director',
  'Manager',
  'Team Lead',
  'Software Engineer',
  'Sales Representative',
  'Marketing Specialist',
  'Product Manager',
  'Data Analyst',
  'Designer',
  'Operations',
  'Human Resources',
  'Finance',
  'Consultant'
];

// Seniority filter options (mapping to possible values of person_job_title_levels)
const DEFAULT_SENIORITY_LEVELS = [
  'C-Level',
  'Vice President',
  'Director',
  'Manager',
  'Team Lead',
  'Staff',
  'Individual Contributor',
  'Owner',
  'Partner'
];

// Add defaults for revenue filtering
const DEFAULT_REVENUE_MIN = 1;   // million
const DEFAULT_REVENUE_MAX = 10000; // million (i.e., $10B)

export const useVisitorsFiltering = (visitors: EnrichedVisitor[]) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<VisitorsFilters>({
    industry: 'all',
    companySize: 'all',
    location: 'all',
    personRole: 'all',
    seniorityLevel: 'all',
    revenueMin: DEFAULT_REVENUE_MIN,
    revenueMax: DEFAULT_REVENUE_MAX,
  });

  // Combine predefined options with actual data from visitors
  const industries = useMemo(() => {
    const uniqueIndustries = new Set<string>(DEFAULT_INDUSTRIES);
    visitors.forEach(visitor => {
      if (visitor.company_industry) {
        uniqueIndustries.add(toPascalCase(visitor.company_industry));
      }
    });
    return Array.from(uniqueIndustries).sort();
  }, [visitors]);

  // Use the default order for company sizes without any custom sorting
  const companySizes = useMemo(() => {
    const uniqueSizes = new Set<string>(DEFAULT_COMPANY_SIZES);
    visitors.forEach(visitor => {
      if (visitor.company_size) {
        uniqueSizes.add(visitor.company_size);
      }
    });
    return Array.from(uniqueSizes);
  }, [visitors]);

  const locations = useMemo(() => {
    const uniqueLocations = new Set<string>(DEFAULT_LOCATIONS);
    visitors.forEach(visitor => {
      if (visitor.location_country) {
        const location = toPascalCase(visitor.location_country);
        uniqueLocations.add(location);
      }
    });
    return Array.from(uniqueLocations).sort();
  }, [visitors]);

  const personRoles = useMemo(() => {
    const uniqueRoles = new Set<string>(DEFAULT_PERSON_ROLES);
    visitors.forEach(visitor => {
      if (visitor.person_job_title_role) {
        uniqueRoles.add(toPascalCase(visitor.person_job_title_role));
      }
    });
    return Array.from(uniqueRoles).sort();
  }, [visitors]);

  // New: extract unique seniority levels
  const seniorityLevels = useMemo(() => {
    const set = new Set<string>(DEFAULT_SENIORITY_LEVELS);
    visitors.forEach(visitor => {
      if (visitor.person_job_title_levels && Array.isArray(visitor.person_job_title_levels)) {
        visitor.person_job_title_levels.forEach(level => {
          set.add(toPascalCase(level));
        });
      }
    });
    return Array.from(set).sort();
  }, [visitors]);

  // New: Find min/max revenue in millions from the actual visitors data
  const allRevenues = visitors
    .map(v => parseCompanyRevenueToMillion(v.company_inferred_revenue))
    .filter((val): val is number => typeof val === 'number');

  // Always ensure minimum revenue starts at at least 1M
  const computedRevenueMin = allRevenues.length ? Math.min(Math.max(Math.min(...allRevenues), 1), DEFAULT_REVENUE_MIN) : DEFAULT_REVENUE_MIN;
  const computedRevenueMax = allRevenues.length ? Math.max(...allRevenues) : DEFAULT_REVENUE_MAX;

  // Helper for parsing company_inferred_revenue e.g. "$10M-$50M" or "$1.2B-$2B" => 10 or 1200 (millions)
  function parseCompanyRevenueToMillion(revenue?: string | null): number | undefined {
    if (!revenue) return undefined;

    // Match patterns like "$10M", "$2.5B", "$10M-$50M", "$2.4B-$10B"
    const match = revenue.match(/\$?([\d.,]+)\s*([MB])(?:\s*-\s*\$?([\d.,]+)\s*([MB]))?/i);
    if (match) {
      // If range, use low end for filtering
      let valueStr = match[1];
      let unit = (match[2] || 'M').toUpperCase();
      let value = parseFloat(valueStr.replace(/,/g, ''));
      if (unit === 'B') {
        value = value * 1000;
      }
      return Math.round(value);
    }

    // Try to extract just a number if present, fallback
    const fallback = parseInt(revenue.replace(/[^0-9]/g, ''), 10);
    return isNaN(fallback) ? undefined : fallback;
  }

  const handleFilterChange = (key: keyof VisitorsFilters, value: string | number) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredVisitors = useMemo(() => {
    return visitors.filter(visitor => {
      // Text search filter
      const matchesSearchTerm = !searchTerm || 
        (visitor.company_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (visitor.company_industry?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (visitor.person_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (visitor.location_country?.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Industry filter
      const matchesIndustry = filters.industry === 'all' || 
        toPascalCase(visitor.company_industry) === filters.industry;
      
      // Company size filter
      const matchesCompanySize = filters.companySize === 'all' || 
        visitor.company_size === filters.companySize;
      
      // Location filter
      const matchesLocation = filters.location === 'all' || 
        toPascalCase(visitor.location_country) === filters.location;
      
      // Person role filter
      const matchesPersonRole = filters.personRole === 'all' ||
        (visitor.person_job_title_role && 
         toPascalCase(visitor.person_job_title_role) === filters.personRole);

      // New: Seniority level filter
      const matchesSeniorityLevel = filters.seniorityLevel === 'all' ||
        (visitor.person_job_title_levels &&
         Array.isArray(visitor.person_job_title_levels) &&
         visitor.person_job_title_levels.some(
           level => toPascalCase(level) === filters.seniorityLevel
         ));

      // Revenue filter (support if present)
      const revenue = parseCompanyRevenueToMillion(visitor.company_inferred_revenue);
      const matchesRevenue =
        (typeof filters.revenueMin === 'undefined' || typeof revenue === 'undefined' || revenue >= filters.revenueMin) &&
        (typeof filters.revenueMax === 'undefined' || typeof revenue === 'undefined' || revenue <= filters.revenueMax);

      return matchesSearchTerm && 
             matchesIndustry && 
             matchesCompanySize && 
             matchesLocation &&
             matchesPersonRole &&
             matchesSeniorityLevel &&
             matchesRevenue;
    });
  }, [visitors, searchTerm, filters]);

  return {
    searchTerm,
    setSearchTerm,
    filters,
    handleFilterChange,
    filteredVisitors,
    industries,
    companySizes,
    locations,
    personRoles,
    seniorityLevels,
    revenueMin: computedRevenueMin,
    revenueMax: computedRevenueMax
  };
};

